import {IABannerProps} from "./IACMDReview";
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Alert,
    Box,
    Button,
    Chip,
    Divider,
    FormControl,
    Grid,
    IconButton,
    Input,
    InputLabel,
    Link,
    MenuItem,
    Select,
    Stack,
    Typography
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import React, {useEffect, useState} from "react";
import {EditorContent} from "../../editor/EditorContent";
import {InternalAffairsVoterDetail} from "../InternalAffairsVoterDetail";
import {genericArrayItemUpdate} from "../../../utils";
import {Link as RouterLink, useNavigate} from "react-router-dom";
import useUser from "../../../hooks/useUser";
import {internalAffairsComplaintOutcome} from "../../../data/internal-affairs/InternalAffairsComplaintOutcome";
import {infractionTypes} from "../../record/IssueInfractionForm";
import {makeApiCall} from "../../../hooks/CancellableApiCall";

interface expandedVoter {
    communityId: string,
    expanded: boolean
}

interface EligibleVoter {
    communityId: string
    nick: string
}

function ReassignVotersSection({displayId}: { displayId: string }) {
    const [eligible, setEligible] = useState<EligibleVoter[]>([])
    const [selected, setSelected] = useState<string[]>([])
    const [loading, setLoading] = useState(false)
    const [success, setSuccess] = useState(false)

    useEffect(() => {
        fetch(`/api/internal-affairs/eligible-voters/${displayId}`)
            .then(r => r.json())
            .then(data => setEligible(data))
            .catch(() => {})
    }, [displayId])

    function reassign() {
        if (selected.length === 0) return
        setLoading(true)
        makeApiCall({
            url: `/api/internal-affairs/reassign-voters/${displayId}`,
            method: "POST",
            body: { voterCommunityIds: selected },
            onLoadedCallback: () => {
                setSuccess(true)
                setSelected([])
            },
            onError: () => setLoading(false)
        })
    }

    const available = eligible.filter(e => !selected.includes(e.communityId))

    if (success) {
        return <>
            <Divider sx={{my: 2}} />
            <Alert severity="success">Voters reassigned successfully. Refresh to see updated votes.</Alert>
        </>
    }

    return <>
        <Divider sx={{my: 2}} />
        <Typography variant="h6" gutterBottom>Reassign Voters</Typography>
        <Box sx={{display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap"}}>
            {selected.map(id => {
                const voter = eligible.find(e => e.communityId === id)
                return <Chip key={id} label={voter?.nick ?? id} size="small" onDelete={() => setSelected(selected.filter(s => s !== id))} />
            })}
            <FormControl size="small" sx={{minWidth: 200}}>
                <InputLabel>Select JCC member...</InputLabel>
                <Select value="" label="Select JCC member..." onChange={e => {
                    const val = e.target.value as string
                    if (val && !selected.includes(val)) setSelected([...selected, val])
                }}>
                    {available.map(v => <MenuItem key={v.communityId} value={v.communityId}>{v.nick}</MenuItem>)}
                </Select>
            </FormControl>
            <Button variant="contained" size="small" disabled={selected.length === 0 || loading} onClick={reassign}>
                Reassign
            </Button>
        </Box>
    </>
}

export const JuniorCCReview = (props: IABannerProps) => {
    const [expanded, setExpanded] = useState(false)
    const [expandedVoters, setExpandedVoters] = useState<expandedVoter[]>([])
    const [outcome, setOutcome] = useState("exonerated")
    const [infraction, setInfraction] = useState("no_action")
    const [note, setNote] = useState("")
    const user = useUser()
    const navigate = useNavigate()

    function findExpandedSate(idx: string): boolean {
        return expandedVoters.find(T => {return T.communityId === idx})?.expanded ?? false
    }

    function isAlreadyRegistered(communityId: string): boolean {
        return expandedVoters.find(T => {return T.communityId === communityId}) !== undefined
    }

    let userVote = props.complaint.votes.find(vote => {
        return (vote.communityId === user.user?.communityId) && vote.voted === null
    })

    if (props.complaint.votes.length === 0) {
        return <>
            <Accordion expanded={expanded}>
                <AccordionSummary>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                            <Stack spacing={2} direction={"row"} sx={{display: "flex", alignItems: "center"}}>
                                <Typography variant={"subtitle1"}>
                                    <Typography sx={{pt: 2}} variant="h6" gutterBottom>Junior Complaint Committee Review</Typography>
                                </Typography>
                                <IconButton
                                    sx={{height: "30px", width: "30px"}}
                                    color="primary"
                                    onClick={() => setExpanded(!expanded)}
                                >
                                    {expanded ? <VisibilityIcon/> : <VisibilityOffIcon/>}
                                </IconButton>
                            </Stack>
                        </Grid>
                    </Grid>
                </AccordionSummary>
                <AccordionDetails>
                    <Alert severity="info">There are no voters on this complaint you may need to refresh the complaint. If this persists (it shouldn't) please send this complaint back and then re-send it to Junior CC review</Alert>
                </AccordionDetails>
            </Accordion>
        </>
    }

    return <>
        {userVote ? <Alert severity="warning">If you are Junior Complaint Committee, or another role without persistent access to voted complaints, after submitting your vote, you will lose access to this complaint</Alert> : <></>}
        <Accordion expanded={expanded}>
            <AccordionSummary>
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                        <Stack spacing={2} direction={"row"} sx={{display: "flex", alignItems: "center"}}>
                            <Typography variant={"subtitle1"}>
                                <Typography sx={{pt: 2}} variant="h6" gutterBottom>Junior Complaint Committee Review</Typography>
                            </Typography>
                            <IconButton
                                sx={{height: "30px", width: "30px"}}
                                color="primary"
                                onClick={() => setExpanded(!expanded)}
                            >
                                {expanded ? <VisibilityIcon/> : <VisibilityOffIcon/>}
                            </IconButton>
                        </Stack>
                    </Grid>
                </Grid>
            </AccordionSummary>
            <AccordionDetails>
                <EditorContent content={props.complaint.finalReport.content} />
                <Divider />
                <Typography variant={"subtitle1"}>
                    <Typography sx={{pt: 2}} variant="h6" gutterBottom>Voters</Typography>
                </Typography>
                <Grid container spacing={2}>
                    {props.complaint.votes && props.complaint.votes.map((vote, idx) => {
                        let expandedVoterDetail = {communityId: vote.communityId, expanded: false}
                        if (!isAlreadyRegistered(vote.communityId) && Number(vote.communityId) > 0) {
                            setExpandedVoters([...expandedVoters, ...[expandedVoterDetail]])
                        }
                        let votedText
                        if (vote.voted === false) {
                            votedText = <Typography color={"error"}>(Did Not Vote)</Typography>
                        } else if (vote.voted === true) {
                            votedText = <Typography>(Voted)</Typography>
                        } else if (vote.voted === null) {
                            votedText = <Typography>(Awaiting Vote)</Typography>
                        }

                        return <Grid key={idx} item xs={4} md={4} lg={4} xl={4}>
                            <Accordion expanded={findExpandedSate(vote.communityId)}>
                                <AccordionSummary>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                                            <Stack spacing={1} direction={"row"} sx={{display: "flex", alignItems: "center"}}>
                                                {Number(vote.communityId) > 0 ? <Link underline="none" component={RouterLink} to={`/user/${vote.communityId}`}>
                                                    {vote.nick}
                                                </Link> : <Typography>{vote.nick}</Typography> }
                                                {votedText}
                                                {vote.outcome !== null ? <IconButton
                                                    sx={{height: "30px", width: "30px"}}
                                                    color="primary"
                                                    onClick={() => {
                                                        setExpandedVoters(genericArrayItemUpdate(
                                                            expandedVoters,
                                                            "communityId",
                                                            vote.communityId,
                                                            "expanded",
                                                            !findExpandedSate(vote.communityId)
                                                        ))
                                                    }}
                                                >
                                                    {findExpandedSate(vote.communityId) ? <VisibilityIcon/> : <VisibilityOffIcon/>}
                                                </IconButton> : <></>}
                                            </Stack>
                                        </Grid>
                                    </Grid>
                                </AccordionSummary>
                                <AccordionDetails><InternalAffairsVoterDetail data={vote}/></AccordionDetails>
                            </Accordion>
                        </Grid>
                    })}
                </Grid>
                {
                    userVote ? <>
                        <Divider />
                        <Typography variant={"subtitle1"}>
                            <Typography sx={{pt: 2}} variant="h6" gutterBottom>Vote</Typography>
                            <Stack sx={{overflowX:"auto"}} spacing={2}>
                                <InputLabel id="outcome-select-label">Outcome</InputLabel>
                                <Select size="small" sx={{width: 500}} id="outcome-select" value={outcome} variant="standard"
                                        onChange={(event) => setOutcome(event.target.value)}
                                >
                                    {internalAffairsComplaintOutcome.map((outcome) =>
                                        <MenuItem key={outcome.key} value={outcome.key}>{outcome.displayName}</MenuItem>
                                    )}
                                </Select>
                                {outcome === "sustained" ? <>
                                    <InputLabel id="infraction-select-label">Infraction</InputLabel>
                                    <Select size="small" sx={{width: 500}} id="infraction-select" value={infraction} variant="standard"
                                            onChange={(event) => setInfraction(event.target.value)}
                                    >
                                        {infractionTypes.map((infractionType) =>
                                            <MenuItem key={infractionType.id} value={infractionType.identifier}>{infractionType.displayName}</MenuItem>
                                        )}
                                    </Select>
                                </> : <></>}
                                <Input multiline rows={2} placeholder={"Note..."} size="small" sx={{width: 1000}}
                                       onChange={(event) => setNote(event.target.value)}
                                ></Input>
                                <Stack spacing={2} direction={"row"} sx={{marginTop: "13px"}}>
                                    <Button variant={"contained"} color="success" onClick={() => {
                                        makeApiCall({
                                            url: `/api/internal-affairs/junior-cc-vote/${props.complaint.displayId}`,
                                            method: "POST",
                                            body: {
                                                outcome: outcome,
                                                actionTaken: infraction,
                                                note: note
                                            },
                                            onLoadedCallback: () => {
                                                navigate(`/internal-affairs-dashboard/assigned`)
                                            },
                                            onError: () => {}
                                        })
                                    }}>Vote</Button>
                                </Stack>
                            </Stack>
                        </Typography>
                    </> : <></>
                }
                {user.hasPermissions(["internalAffairs:complaintSeniorReviewBoard"]) && <ReassignVotersSection displayId={props.complaint.displayId} />}
            </AccordionDetails>
        </Accordion>
    </>
}
